import type { DateTime } from 'luxon'
import type {
  ContainerWatchState,
  GETShippingLine,
  ShippingLine,
} from '~/services/apiClient'

/**
 * What a shipping line that withholds its LFD from terminals needs the UI to say.
 *
 * Backend config (DEV-8198), not a frontend constant: adding a line is a backend edit
 * plus a deploy, with no frontend change (PRD #276 BR-3). It replaces the hardcoded
 * `LINE_LFD_GAP_INFO` map that used to live in `containers.ts`.
 */
export interface LineLFDGap {
  note: string
  /**
   * Absent where the line has no page a user can actually read an LFD off of —
   * Maersk puts it behind a login, and a link that leads nowhere useful is worse
   * than no link.
   */
  lookupUrl?: string
  /**
   * States in which a missing line LFD is worth flagging. Comes from the same list
   * the backend's `needs_line_lfd_check` filter runs on, so the badge and the filter
   * cannot disagree — that drift is what DEV-7154 cost us the last time each side
   * kept its own list.
   */
  checkStates: Set<ContainerWatchState>
}

export type LineLFDGapConfig = ReadonlyMap<ShippingLine, LineLFDGap>

/**
 * The config before `/containers/shipping_lines/` has answered, and the value to pass
 * anywhere the gap simply does not apply. No line warns, so an unloaded store fails
 * quiet rather than fails wrong.
 */
export const NO_LINE_LFD_GAPS: LineLFDGapConfig = new Map()

/**
 * Whether to caveat the LFD on screen: the line withholds its own, and we have none.
 *
 * Not gated on cycle state \u2014 the caveat has to reach every place we render an LFD,
 * and the drawer's per-source panel renders one in any state. Callers that only
 * display an LFD in some states gate themselves.
 */
export function lineWithholdsLFD(
  gap: LineLFDGap | undefined,
  lineLastFreeDay: DateTime | null | undefined
): boolean {
  return Boolean(gap) && !lineLastFreeDay
}

/**
 * Whether this is a container the user should go and check the line's LFD for \u2014 the
 * caveat above, narrowed to the states the backend's `needs_line_lfd_check` filter
 * uses.
 *
 * One predicate for all four callers (the Imports badge and live-update filter, the
 * Scheduled cell, badge and board filter) on purpose: a badge must always mean the
 * container is in the filtered set and vice versa, and four hand-written copies of
 * this expression is exactly how the badge drifted from the filter before.
 */
export function needsLineLFDCheck(
  gap: LineLFDGap | undefined,
  state: ContainerWatchState,
  lineLastFreeDay: DateTime | null | undefined
): boolean {
  if (!lineWithholdsLFD(gap, lineLastFreeDay)) return false
  return gap!.checkStates.has(state)
}

export function buildLineLFDGapConfig(
  lines: GETShippingLine[]
): LineLFDGapConfig {
  const config = new Map<ShippingLine, LineLFDGap>()
  for (const line of lines) {
    if (line.shares_lfd_with_terminals) continue
    // Typed non-null by the generator, but the spec marks it nullable, and the note is
    // the entire warning — with nothing to say we would render an empty tooltip on a
    // badge the user cannot act on. `shippingLinesStore` reports the misconfiguration
    // to Sentry when the response lands; this module stays free of that import so it
    // can be reached from the models without dragging Sentry into `test/setup.ts`.
    if (!line.lfd_gap_note) continue
    config.set(line.shipping_line, {
      note: line.lfd_gap_note,
      lookupUrl: line.lfd_lookup_url || undefined,
      checkStates: new Set(line.lfd_check_states),
    })
  }
  return config
}
